import { useFetchEQLReferences } from '@modules/queryBuilder/hooks';
import { captureMessage } from '@sentry/react';
import { useEffect } from 'react';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { mapToEQL } from 'v2/core/repository/eql/mappers';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { getHumanReadableEQLString } from '../utils';
import { useOpenAIChatCompletions } from './useOpenAIChatCompletions';

interface GetCWFPromptSuggestionsOptions {
  queryContainer: QueryContainer;
  skip?: boolean;
}

interface OpenAIPromptResponse {
  prompts: {
    chainOfThought: string;
    useCase: string;
    prompt: string;
  }[];
}

interface WisdomPrompts {
  prompt: string;
  boldedTexts: string[];
}

const OPENAI_PROMPT = `
# Mission
You are a world class customer feedback analyst. Your mission is to identify different use-cases to derive insights from a list of filtered feedback, and write prompts to satisfy those use-cases.

 # Context
- A product manager has filtered customer feedback on their product to their area of interest using EQL.
- The product manager has use-cases in their mind, that they want to satisfy by finding insights from the list of filtered feedback.
- This product manager is analysing this feedback using an AI-powered chatbot but they are struggling to articulate their use-case in an effective prompt to the chatbot.

## Chatbot's limitations
- The chatbot can only answer based the natural language content of the feedback.
- It can't look at feedback trend, or metadata values associated with the feedback, like who's the user who shared feedback, etc.

## EQL
- An EQL is a string that represents a subset of  customer feedback. The string can contain special filters like -
   - Entity: An entity is an noun mentioned in customer feedback on a product. It can also be a feature, an aspect like performance, or a competitor's name.
   - Category: Category captures the user intent for sharing feedback. It can be issue, help, improvement, or praise.
   - Reason: a Reason is why customer shared feedback -- these are repeatable summaries.
   - Source: Represents the source of customer feedback or platform where the feedback was posted by customer.
   - Metadata: Metadata would have different names, and would be different tags and properties of the feedback, ingested directly from the feedback source.
- "entity", "category", "reason" and "feedback like"  filters match attributes derived directly from the content of the feedback. These are especially helpful to generate prompts that can be answered using the content of feedback.

### EQL Examples
Example
EQL string: select records where (((category = any ('issue')) AND (entity having categories ('issue') = any ('Audio','Video')))) timerange(now, 3month)
Explanation: Feedback where customers have expressed issue with Audio and Video part of product within last 3 months.

# Steps
- you will be provided with an EQL string as input
- based on the EQL string, identify use-cases that the product manager wants to achieve using customer feedback
- for each use-case, generate a prompt that the product manager can share with chatbot to analyse the filtered customer feedback, and get to the insight they want to
- respond with a valid JSON, in the format specified below
- for each use-case and prompt, ensure that you provide a chain-of-thought from first-principles for why the use-case is valid, and why the prompt would help satisfy the use-case

# Rules
- ONLY respond with prompts for which a response can be generated solely by analysing the actual content of feedback.
- DO NOT use the name of the source in the prompt, since it doesn't help analyse the content of the feedback.
- Make sure that the prompt is brief, and to the point. DO NOT use any fluff words. Think deeply before including any words in your response.
- If there are any "entity", "reason", "feedback like" or "category" filters in the EQL, then identify use-cases based on them, and respond with a prompt -- its okay to ignore all other filters.
- Be highly specific, instruct the chatbot to respond in the exact format you want. Make the format as useful as possible for the product manager.
- The prompt suggestion would be shown as a dropdown, and NEEDS to fit in single line of text. Use short, simple sentences. The fewer words the better.
- Include the portion of the prompt that highlights the product manager's use-case  in "*" asterisks, so that the user can identify the use-case by immediately looking at the prompt

# Examples of good prompts
- Provide a *comprehensive overview of the feedback for [product area]*. Group findings into positive aspects and pain points. For each point, include a brief explanation and citations.
- *Summarize key themes in [product area]* feedback. Highlight top positives and negatives.
- Create a detailed feedback overview. How do users feel about [product area] overall? *What drives positive and negative emotions*?
- *Analyze complaints about [issue]. Identify potential root causes* and group them into themes. Provide supporting evidence and citations.
- *What's the real problem behind [issue]?* Look past the surface complaints and find the underlying pain
- *Create a 'voice of the customer' summary on [product area]*. What's the overarching narrative? Identify key themes, unexpected insights, and areas ripe for innovation.
- *Synthesize feedback into a sharable slack message for [product area]*. Identify complaints, feature requests, and the overall user sentiment with citations.
- Summarize user feedback for [product area]. *Create a list of the most impactful potential roadmap items*. Support each item with citations.
-  *Generate actionable recommendations to improve customer satisfaction for [product area]*. Provide a brief rationale and potential impact for each.
- *Summarize user struggles with [process/workflow]*. What's the biggest pain point?
- *Analyze complaints about [specific process]. What are the most common causes for user frustration?* Provide examples of user struggles and suggest potential improvements.

# Input
- EQL string

# Output
- JSON array of identified prompts

## Output Format
\`\`\`json
{{
    "prompts": [
        {{
               "chainOfThought": <chain of thought for why the use-case is valid, and how the prompt would help>,
               "useCase": <use-case>,
               "prompt" : <prompt>
        }}
       ...
]
}}
\`\`\`
`;

const ASTERISKS_REGEX = new RegExp('\\*([^*]+)\\*', 'g');

export const useFetchWisdomPrompts = ({
  queryContainer,
  skip = false,
}: GetCWFPromptSuggestionsOptions) => {
  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  const [
    fetchChatResponse,
    {
      chatResponse,
      isLoading: isLoadingOpenAIResponse,
      error: chatCompletionError,
    },
  ] = useOpenAIChatCompletions();

  const { eqlFieldByID, isLoading: areLoadingEQLReferences } =
    useFetchEQLReferences({
      whereClause: queryContainer.WhereClause,
      skip: skip,
    });

  const humanReadableEQLString = getHumanReadableEQLString(
    mapToEQL({
      expressionType: ExpressionType.Records,
      sourceDetailsMap: sourceDetailsMap ?? new Map(),
      whereClause: queryContainer.WhereClause,
      aggregatePeriod: queryContainer.AggregatePeriod,
      dateRange: queryContainer.DateRange,
    }),
    eqlFieldByID,
  );

  const isLoading =
    areLoadingEQLReferences ||
    !Object.keys(eqlFieldByID).length ||
    !sourceDetailsMap;

  useEffect(() => {
    if (isLoading || skip) {
      return;
    }

    fetchChatResponse({
      model: 'gpt-4o',
      messages: [
        {
          role: 'system',
          content: OPENAI_PROMPT,
        },
        {
          role: 'user',
          content: humanReadableEQLString,
        },
      ],
    });
  }, [fetchChatResponse, isLoading, humanReadableEQLString, skip]);

  const getWisdomPrompts = (): WisdomPrompts[] | undefined => {
    if (!chatResponse || isLoadingOpenAIResponse) {
      return;
    }

    const { content } = chatResponse;
    try {
      // Remove ```json from the content
      const formattedContent = content.replace(/```json|```/g, '');
      // Parse the content on basis of how we have defined in input prompt
      const parsedJSON = JSON.parse(formattedContent) as OpenAIPromptResponse;
      // Get the string array of suggested prompts
      return parsedJSON.prompts.map(({ prompt }) => {
        const boldWords: string[] = [];
        const formattedPrompt = prompt.replace(
          ASTERISKS_REGEX,
          (_, boldText) => {
            boldWords.push(boldText); // capture the text inside the asterisks
            return boldText; // replace the asterisks with the inner text
          },
        );

        return {
          prompt: formattedPrompt,
          boldedTexts: boldWords,
        };
      });
    } catch (err) {
      captureMessage('error while parsing CWF OpenAI response', {
        extra: {
          content,
          err,
          humanReadableEQLString,
        },
        level: 'info',
      });
    }
  };

  return {
    prompts: getWisdomPrompts(),
    loading: isLoadingOpenAIResponse || areLoadingEQLReferences,
    error: chatCompletionError,
  } as const;
};
